import React, { useEffect, useState, useCallback } from 'react';
import { TabsContent } from '../../../components/ui/tabs';
import { Loader2, AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { forestaOpsApi } from '../../../lib/api';
import { formatDate } from '../../../lib/utils';
import { MonthPeriodHeader } from '../../../components/common/MonthPeriodHeader';

const ESTADO_TONE = {
  moroso: 'bg-red-50 text-red-700 ring-red-100',
  parcial: 'bg-amber-50 text-amber-700 ring-amber-100',
  pendiente: 'bg-orange-50 text-orange-700 ring-orange-100',
  observado: 'bg-amber-50 text-amber-700 ring-amber-100',
  pagado: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  validado: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
};

const fmtMoney = (n) => n == null ? '—' : new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);

export const TabMorososForesta = ({ edificioId, periodo, setSelectedPeriodo, activeTab }) => {
  const [data, setData] = useState({ items: [], totales: {}, config: {} });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null); // {unidad_id, periodo, unidad_nombre}
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const r = await forestaOpsApi.listMorosos(edificioId, periodo, 12);
      setData(r.data);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || 'Error al cargar morosos');
    }
    finally { setLoading(false); }
  }, [edificioId, periodo]);

  useEffect(() => {
    if (activeTab !== 'morosos') return;
    load();
  }, [activeTab, load]);

  const handleConfirmDelete = async () => {
    if (!deleteDialog) return;
    setDeleting(true);
    try {
      await forestaOpsApi.excluirMoroso(edificioId, deleteDialog.unidad_id, deleteDialog.periodo);
      toast.success('Registro de morosidad eliminado');
      setDeleteDialog(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al eliminar el registro');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <TabsContent value="morosos" className="mt-0">
      <div className="space-y-3 flex-1 min-h-0 flex flex-col">
        {setSelectedPeriodo && (
          <MonthPeriodHeader
            value={periodo}
            onChange={setSelectedPeriodo}
            testid="foresta-header-morosos"
            right={
              <div className="flex items-center gap-6 text-sm" data-testid="morosos-summary-inline">
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wide">Capital</span>
                  <span className="font-semibold text-slate-900">{fmtMoney(data.totales?.capital)}</span>
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wide">Intereses</span>
                  <span className="font-semibold text-red-600">{fmtMoney(data.totales?.intereses)}</span>
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wide">Total</span>
                  <span className="font-bold text-slate-900">{fmtMoney(data.totales?.total)}</span>
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wide">Items</span>
                  <span className="font-semibold text-slate-700">{data.totales?.cantidad ?? 0}</span>
                </div>
              </div>
            }
          />
        )}

        <div className="bg-white rounded-lg border border-slate-200 flex-1 min-h-0 flex flex-col overflow-hidden">
          {errorMsg ? (
            <div className="p-10 text-center text-sm" data-testid="morosos-foresta-error">
              <AlertTriangle className="w-6 h-6 text-rose-500 mx-auto mb-2" />
              <div className="text-rose-700 font-medium">{errorMsg}</div>
              <button onClick={load} className="mt-3 text-xs text-indigo-600 hover:underline">Reintentar</button>
            </div>
          ) : loading ? (
            <div className="p-10 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : data.items.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">No hay morosos para el período {periodo} y los 12 meses previos.</div>
          ) : (
            <div className="overflow-auto flex-1 min-h-0">
              <table className="w-full text-sm min-h-full">
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200">
                    <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Unidad</th>
                    <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Propietario</th>
                    <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Inquilino</th>
                    <th className="text-center text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Período</th>
                    <th className="text-center text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Venc.</th>
                    <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Alquiler</th>
                    <th className="text-center text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Días</th>
                    <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Intereses</th>
                    <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Total</th>
                    <th className="text-center text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Estado</th>
                    <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it, i) => (
                    <tr key={`${it.unidad_id}-${it.periodo}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`moroso-row-${it.unidad_id}-${it.periodo}`}>
                      <td className="py-2.5 px-3 font-medium text-slate-900 whitespace-nowrap">{it.unidad_nombre}</td>
                      <td className="py-2.5 px-3 text-slate-700 max-w-[180px] truncate" title={it.propietario_nombre}>{it.propietario_nombre || '—'}</td>
                      <td className="py-2.5 px-3 text-slate-700 max-w-[160px] truncate" title={it.inquilino || ''}>{it.inquilino || '—'}</td>
                      <td className="py-2.5 px-2 text-center text-slate-700">{it.periodo}</td>
                      <td className="py-2.5 px-2 text-center text-slate-700 whitespace-nowrap">{formatDate(it.vencimiento)}</td>
                      <td className="py-2.5 px-2 text-right text-slate-700 whitespace-nowrap">{fmtMoney(it.alquiler_adeudado)}</td>
                      <td className="py-2.5 px-2 text-center">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium ${it.dias_atraso > 0 ? 'text-red-700 bg-red-50' : 'text-slate-500'}`}>
                          {it.dias_atraso > 0 ? <AlertTriangle className="w-3 h-3" /> : null}
                          {it.dias_atraso}d
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-right text-red-600 whitespace-nowrap">{fmtMoney(it.intereses)}</td>
                      <td className="py-2.5 px-2 text-right font-semibold text-slate-900 whitespace-nowrap">{fmtMoney(it.total_adeudado)}</td>
                      <td className="py-2.5 px-2 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ring-1 capitalize ${ESTADO_TONE[it.estado] || ESTADO_TONE.pendiente}`}>
                          {it.estado}
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                          title="Eliminar registro de morosidad"
                          onClick={() => setDeleteDialog({ unidad_id: it.unidad_id, periodo: it.periodo, unidad_nombre: it.unidad_nombre })}
                          data-testid={`moroso-delete-${it.unidad_id}-${it.periodo}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Confirm delete */}
      <AlertDialog open={!!deleteDialog} onOpenChange={(o) => !o && setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este registro de morosidad?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción solo quitará esta fila de Morosos para la unidad <strong>{deleteDialog?.unidad_nombre}</strong> del período <strong>{deleteDialog?.periodo}</strong>.
              <br /><br />
              No se borra el contrato, la unidad, el propietario, el inquilino, ni las facturas/comprobantes asociados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="moroso-delete-confirm"
            >
              {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Eliminando...</> : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TabsContent>
  );
};

export default TabMorososForesta;
